import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { useToast } from '../hooks/use-toast';
import { DollarSign, Users, Link2, BarChart3, Check, ArrowRight } from 'lucide-react';
import axios from 'axios';

import { BACKEND_URL } from "../lib/backendUrl";
const API = `${BACKEND_URL}/api`;

const BENEFITS = [
  { icon: DollarSign, title: '10% Commission', desc: 'Earn 10% recurring commission on every referred customer' },
  { icon: Users, title: 'No Limit', desc: 'Refer as many customers as you want — unlimited earnings' },
  { icon: Link2, title: 'Unique Link', desc: 'Get your own branded referral link: miadesk.com/ref/yourname' },
  { icon: BarChart3, title: 'Real-Time Stats', desc: 'Track clicks, signups, and earnings from your dashboard' },
];

const AffiliateSignup = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [form, setForm] = useState({ name: '', email: '', username: '', website: '', social_media: '', reason: '' });
  const [loading, setLoading] = useState(false);
  const [usernameStatus, setUsernameStatus] = useState(null);
  const [success, setSuccess] = useState(null);

  const checkUsername = async (val) => {
    const u = val.toLowerCase().replace(/[^a-z0-9]/g, '');
    setForm(f => ({ ...f, username: u }));
    if (u.length < 3) { setUsernameStatus(null); return; }
    try {
      const res = await axios.get(`${API}/affiliate/check-username/${u}`);
      setUsernameStatus(res.data.available ? 'available' : 'taken');
    } catch { setUsernameStatus(null); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (usernameStatus === 'taken') { toast({ title: 'Username taken', variant: 'destructive' }); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/affiliate/signup`, form);
      setSuccess(res.data);
      toast({ title: 'Welcome to the Affiliate Program!' });
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Signup failed', variant: 'destructive' });
    } finally { setLoading(false); }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0a1628] via-[#0d1f35] to-[#0a1628] flex items-center justify-center px-6 py-12" data-testid="affiliate-success">
        <Card className="w-full max-w-lg bg-gradient-to-br from-emerald-900/30 to-teal-900/20 border-emerald-500/20">
          <CardContent className="py-12 text-center space-y-6">
            <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto">
              <Check className="w-8 h-8 text-emerald-400" />
            </div>
            <h2 className="text-2xl font-bold text-white">You're In!</h2>
            <p className="text-gray-400">Your affiliate account is active. Here's your referral link:</p>
            <div className="bg-black/30 rounded-xl p-4 border border-emerald-500/20">
              <p className="text-xs text-gray-500 mb-1">Your Referral Link</p>
              <p className="text-lg font-mono text-emerald-400 font-bold" data-testid="referral-link">
                {window.location.origin}/ref/{success.username}
              </p>
            </div>
            <div className="flex gap-3 justify-center">
              <Button onClick={() => navigator.clipboard.writeText(`${window.location.origin}/ref/${success.username}`).then(() => toast({ title: 'Link copied!' }))} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="copy-link-btn">
                Copy Link
              </Button>
              <Button variant="outline" onClick={() => navigate(`/affiliate/dashboard/${success.username}`)} className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10" data-testid="go-to-dashboard-btn">
                Go to Dashboard <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a1628] via-[#0d1f35] to-[#0a1628]" data-testid="affiliate-signup-page">
      {/* Header */}
      <header className="border-b border-white/5 bg-[#0a1628]/80 backdrop-blur-lg">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 hover:opacity-80 transition-opacity" data-testid="affiliate-logo">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center shadow-lg shadow-purple-500/25">
              <span className="text-white font-bold text-xs">MD</span>
            </div>
            <span className="text-white font-semibold">Miadesk</span>
          </Link>
          <Link to="/login" className="text-sm text-gray-400 hover:text-white transition-colors">Already an affiliate? Sign in</Link>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-16">
        {/* Hero */}
        <div className="text-center mb-16">
          <span className="inline-flex items-center px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs font-medium mb-4">
            Affiliate Program
          </span>
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">Earn With Miadesk</h1>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto">
            Refer businesses to Miadesk and earn 10% recurring commission on every subscription. No caps, no limits.
          </p>
        </div>

        {/* Benefits */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-16">
          {BENEFITS.map((b) => (
            <div key={b.title} className="p-5 rounded-xl bg-white/[0.03] border border-white/[0.06] hover:border-emerald-500/20 transition-colors" data-testid={`benefit-${b.title.toLowerCase().replace(/\s+/g, '-')}`}>
              <b.icon className="w-6 h-6 text-emerald-400 mb-3" />
              <h3 className="text-white font-semibold text-sm mb-1">{b.title}</h3>
              <p className="text-gray-500 text-xs leading-relaxed">{b.desc}</p>
            </div>
          ))}
        </div>

        {/* Signup Form */}
        <Card className="max-w-lg mx-auto bg-gradient-to-br from-emerald-900/20 to-teal-900/10 border-emerald-500/10" data-testid="affiliate-form-card">
          <CardHeader>
            <CardTitle className="text-white text-xl">Join the Program</CardTitle>
            <CardDescription className="text-gray-400">Fill in your details — you'll be approved instantly.</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300 text-xs">Full Name *</Label>
                  <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required placeholder="John Doe" className="bg-black/30 border-white/10 text-white mt-1" data-testid="affiliate-name" />
                </div>
                <div>
                  <Label className="text-gray-300 text-xs">Email *</Label>
                  <Input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required type="email" placeholder="john@example.com" className="bg-black/30 border-white/10 text-white mt-1" data-testid="affiliate-email" />
                </div>
              </div>

              <div>
                <Label className="text-gray-300 text-xs">Username * <span className="text-gray-500">(used in your referral link)</span></Label>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-gray-500 text-sm whitespace-nowrap">miadesk.com/ref/</span>
                  <Input value={form.username} onChange={e => checkUsername(e.target.value)} required minLength={3} placeholder="johndoe" className="bg-black/30 border-white/10 text-white" data-testid="affiliate-username" />
                </div>
                {usernameStatus === 'available' && <p className="text-emerald-400 text-xs mt-1">Username available</p>}
                {usernameStatus === 'taken' && <p className="text-red-400 text-xs mt-1">Username already taken</p>}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300 text-xs">Website</Label>
                  <Input value={form.website} onChange={e => setForm({ ...form, website: e.target.value })} placeholder="https://yourblog.com" className="bg-black/30 border-white/10 text-white mt-1" data-testid="affiliate-website" />
                </div>
                <div>
                  <Label className="text-gray-300 text-xs">Social Media</Label>
                  <Input value={form.social_media} onChange={e => setForm({ ...form, social_media: e.target.value })} placeholder="@johndoe" className="bg-black/30 border-white/10 text-white mt-1" data-testid="affiliate-social" />
                </div>
              </div>

              <Button type="submit" disabled={loading || usernameStatus === 'taken'} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="affiliate-submit-btn">
                {loading ? 'Creating account...' : 'Join the Affiliate Program'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default AffiliateSignup;
